import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bot, Loader2 } from 'lucide-react';
import { ArtifactRenderer } from '@/components/ArtifactRenderer';
import { cn } from '@/lib/utils';
import type { Artifact } from '@/types';

interface StreamingMessageProps {
  content: string;
  isComplete: boolean;
  timestamp: Date;
  artifacts?: Artifact[];
  className?: string;
}

export function StreamingMessage({ 
  content, 
  isComplete, 
  timestamp, 
  artifacts = [],
  className 
}: StreamingMessageProps) {
  const [displayContent, setDisplayContent] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  // Animate content appearance for better UX
  useEffect(() => {
    if (content.length === 0) {
      setDisplayContent('');
      return;
    }

    // If content is complete, show it all immediately
    if (isComplete) {
      setDisplayContent(content);
      setShowCursor(false);
      return;
    }

    // Otherwise, animate the typing effect
    setDisplayContent(content);
    setShowCursor(true);
  }, [content, isComplete]);

  // Blinking cursor effect
  useEffect(() => {
    if (isComplete) return;

    const interval = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 500);

    return () => clearInterval(interval);
  }, [isComplete]);

  return (
    <div className={cn('flex gap-3 max-w-4xl mx-auto px-4 py-3', className)}>
      {/* Avatar */}
      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center">
        {isComplete ? (
          <Bot className="h-4 w-4" />
        ) : (
          <Loader2 className="h-4 w-4 animate-spin" />
        )}
      </div>

      {/* Message Content */}
      <div className="flex-1 min-w-0">
        {/* Message Header */}
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-medium">Assistant</span>
          <span className="text-xs text-muted-foreground">
            {timestamp.toLocaleTimeString([], { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </span>
          {!isComplete && (
            <Badge variant="secondary" className="text-xs">
              <Loader2 className="h-2 w-2 mr-1 animate-spin" />
              Streaming
            </Badge>
          )}
        </div>

        {/* Message Bubble */}
        <Card className="max-w-[80%] bg-muted/50">
          <CardContent className="p-3">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <p className="whitespace-pre-wrap break-words m-0">
                {displayContent}
                {!isComplete && showCursor && (
                  <span className="inline-block w-0.5 h-4 bg-primary ml-0.5 animate-pulse" />
                )}
              </p>
            </div>

            {/* Artifacts */}
            {artifacts && artifacts.length > 0 && (
              <div className="mt-4 space-y-3">
                {artifacts.map((artifact, index) => (
                  <ArtifactRenderer key={index} artifact={artifact} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}